import { unminifyError } from '@sgme/stacktrace';
import type { Observable, SchedulerLike } from 'rxjs';
import { from, throwError } from 'rxjs';
import { fromFetch } from 'rxjs/fetch';
import { catchError, map, mergeMap, timeoutWith } from 'rxjs/operators';
import { getSgmeConfiguration } from 'state/configureStore';
import { getFrontLogger } from 'utils/loggers/FrontLogger';

import { getContentDispositionFileName } from './contentDisposition';
import type { HttpCall, HttpError, IHttpClient, IHttpConfig } from './httpClient.model';
import { HttpTimeoutError, isFormData } from './httpClient.model';
import {
  useRequestMiddlewares as applyRequestMiddlewares,
  useResponseMiddlewares as applyResponseMiddlewares,
} from './httpMiddlewares';

export const httpClient: IHttpClient = {
  patch<T>(config: IHttpConfig): Observable<HttpCall<T>> {
    return sendRequest<T>(config, 'PATCH', getJsonPayload);
  },
  post(config: IHttpConfig): Observable<HttpCall<void>> {
    return sendRequest(config, 'POST', getNoPayload);
  },
  postJSON<T>(config: IHttpConfig): Observable<HttpCall<T>> {
    return sendRequest<T>(config, 'POST', getJsonPayload);
  },
  postRAW(config: IHttpConfig): Observable<HttpCall<string>> {
    return sendRequest(config, 'POST', getTextPayload);
  },
  postBlob(config: IHttpConfig): Observable<HttpCall<Blob>> {
    return sendRequest(config, 'POST', getBlobPayload);
  },
  put(config: IHttpConfig): Observable<HttpCall<void>> {
    return sendRequest(config, 'PUT', getNoPayload);
  },
  head(config: IHttpConfig): Observable<HttpCall<void>> {
    return sendRequest(config, 'HEAD', getNoPayload);
  },
  putJSON<T>(config: IHttpConfig): Observable<HttpCall<T>> {
    return sendRequest<T>(config, 'PUT', getJsonPayload);
  },
  putRaw(config: IHttpConfig): Observable<HttpCall<string>> {
    return sendRequest<string>(config, 'PUT', getTextPayload);
  },
  delete(config: IHttpConfig): Observable<HttpCall<void>> {
    return sendRequest(config, 'DELETE', getNoPayload);
  },
  deleteJSON<T>(config: IHttpConfig): Observable<HttpCall<T>> {
    return sendRequest<T>(config, 'DELETE', getJsonPayload);
  },
  getJSON<T>(config: IHttpConfig): Observable<HttpCall<T>> {
    return sendRequest<T>(config, 'GET', getJsonPayload);
  },
  getRAW(config: IHttpConfig): Observable<HttpCall<string>> {
    return sendRequest(config, 'GET', getTextPayload);
  },
  getBlob(config: IHttpConfig): Observable<HttpCall<Blob>> {
    return sendRequest(config, 'GET', getBlobPayload);
  },
};

function sendRequest<T>(
  config: IHttpConfig,
  method: 'POST' | 'GET' | 'PUT' | 'DELETE' | 'PATCH' | 'HEAD',
  getPayload: (response: Response) => Promise<T>,
  scheduler?: SchedulerLike,
): Observable<HttpCall<T>> {
  const start = performance.now();
  config = applyRequestMiddlewares(config);
  const request = createRequest(config, method);
  return fromFetch(request).pipe(
    catchError(err => {
      const end = performance.now();
      getFrontLogger().error('An error occurred while querying backend {@FrontError}', {
        status: err.status,
        correlationId: config.headers?.['X-Correlation-ID'],
        frontDoorRef: config.headers?.['x-azure-ref'],
        url: config.url,
        timeTaken: end - start,
        errorMessage: err.message,
        stack: JSON.stringify(err.stack),
        fullError: JSON.stringify(err),
      });
      throw <HttpError>{
        name: err.name,
        status: err.status,
        ok: false,
        correlationId: config.headers?.['X-Correlation-ID'],
        url: config.url,
        timeTaken: end - start,
        message: `a communication error occurred : ${method} ${config.url}`,
      };
    }),
    applyTimeout(config, scheduler),
    map(applyResponseMiddlewares()),
    mergeMap(response => from(mapResponse(response, start, getPayload, config))),
  );
}

function createRequest(config: IHttpConfig, method: string): Request {
  const isFormDataBody = isFormData(config.body);

  const { 'Content-Type': _removed, ...headersWithoutContentType } = config.headers ?? {};
  const headers = isFormDataBody ? headersWithoutContentType : config.headers;

  let body: BodyInit | null = null;
  if (isFormDataBody) {
    body = config.body as FormData;
  } else if (config.body) {
    body = JSON.stringify(config.body);
  }

  return new Request(config.url, {
    method,
    headers: headers as Record<string, string>,
    body,
  });
}

async function mapResponse<T>(
  response: Response,
  start: number,
  getPayload: (response: Response) => Promise<T>,
  config: IHttpConfig,
): Promise<HttpCall<T>> {
  const end = performance.now();
  if (response.status === 204 || (config.expect404NotFound === true && response.status === 404)) {
    return {
      payload: undefined as any as T,
      timeTaken: end - start,
      attachmentName: undefined,
      status: response.status,
      ok: response.ok,
    };
  }

  if (response.ok) {
    const attachmentName = getContentDispositionFileName(response.headers);
    try {
      const payload = await getPayload(response);
      return {
        payload,
        timeTaken: end - start,
        attachmentName,
        status: response.status,
        ok: response.ok,
      };
    } catch (exception) {
      const ex = await unminifyError(exception);
      getFrontLogger().error(
        'An error occurred while reading response payload {@FrontError} : {@Exception}',
        {
          status: response.status,
          correlationId: config.headers?.['X-Correlation-ID'],
          url: config.url,
          timeTaken: end - start,
        },
        ex,
      );
      throw exception;
    }
  } else {
    const traceIdHeaderName = getSgmeConfiguration().traceIdHeaderName;

    const error: HttpError<unknown> = {
      name: 'HttpError',
      status: response.status,
      timeTaken: end - start,
      message: 'a communication error occurred',
      correlationId: config.headers?.['X-Correlation-ID']!,
      traceId: traceIdHeaderName ? (response.headers.get(traceIdHeaderName) ?? undefined) : undefined,
      url: config.url,
      ok: response.ok,
    };

    if (config.parseErrorPayload && config.tryParseErrorPayload !== true) {
      const contentType = response.headers.get('Content-Type');
      const contentLength = response.headers.get('Content-Length');
      const isInvalidTimeoutResponse =
        (!contentType || !contentType.includes('application/json') || contentLength === '0') && response.status === 504;
      error.payload = isInvalidTimeoutResponse ? getPayloadFromInvalidJsonResponse() : await getJsonPayload(response);
    } else if (config.tryParseErrorPayload === true) {
      try {
        error.payload = config.useTextErrorPayload ? await getTextPayload(response) : await getJsonPayload(response);
      } catch {
        error.payload = undefined;
      }
    }
    throw error;
  }
}

function getPayloadFromInvalidJsonResponse() {
  return {
    rejections: [
      {
        originMessage: 'Request timeout. Please contact support for assistance.',
        rejectionCode: 'TIMEOUT',
        rejectionMessage: 'Request timeout',
        rejectionOrigin: 'SG Markets',
      },
    ],
  };
}
async function getNoPayload(_: Response): Promise<void> {
  return Promise.resolve();
}

async function getJsonPayload<T>(response: Response): Promise<T> {
  return (await response.json()) as T;
}

async function getTextPayload(response: Response): Promise<string> {
  return await response.text();
}

async function getBlobPayload(response: Response): Promise<Blob> {
  return await response.blob();
}

function applyTimeout(config: IHttpConfig, scheduler?: SchedulerLike) {
  return function operator<T>(source$: Observable<T>): Observable<T> {
    if (config.timeOut) {
      return source$.pipe(
        timeoutWith(
          config.timeOut,
          throwError(
            new HttpTimeoutError(config.timeOut, config.timeOutErrorMessage, config.headers?.['X-Correlation-ID']),
          ).pipe(
            catchError(err => {
              getFrontLogger().error('An timeout occurred while querying backend {@FrontError}', {
                status: err.status,
                correlationId: config.headers?.['X-Correlation-ID'],
                url: config.url,
                timeout: config.timeOut,
              });
              throw err;
            }),
          ),
          scheduler,
        ),
      );
    }
    return source$;
  };
}
